import { motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { BrackyFeatureSection } from "./BrackyFeatureSection"
import { BrackyHeroQuote } from "./BrackyHeroQuote"
import { brackyFeatures } from "./data/bracky-features-data"
import { useEllipsisAnimation } from "./hooks/useEllipsisAnimation"
import { LandingArc } from "./LandingArc"
import { LandingBrackyAdvancedFeatures } from "./LandingBrackyAdvancedFeatures"
import { LandingChipSectionHeader } from "./LandingChipSectionHeader"
import { LandingCTA } from "./LandingCTA"
import { LandingIconAnimatedActivity } from "./LandingIconAnimatedActivity"
import { LandingAskBracky } from "./LandingPromptExamples"

export function LandingSectionBracky({ fanCount = "72K+" }: { fanCount?: string } = {}) {
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const askRef = useRef<HTMLDivElement | null>(null)
  const arcRef = useRef<HTMLDivElement | null>(null)
  const fansLiveRef = useRef<HTMLDivElement | null>(null)
  const brackyHeroRef = useRef<HTMLDivElement | null>(null)

  const [_hasScrolled, setHasScrolled] = useState<boolean>(false)
  const [_stadiumLightsOn, setStadiumLightsOn] = useState(false)
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 725 : false)
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 640 : true)
  const [isBrackyHeroVisible, setIsBrackyHeroVisible] = useState(false)
  const navLogoScrollRef = useRef(false)
  const [activeFeatureIdx, setActiveFeatureIdx] = useState(0)

  const {
    ellipsisActive,
    setEllipsisActive,
    liveSportsTrending,
    setLiveSportsTrending,
    typingStarted,
    ellipsisRef,
    heroSeenRef,
  } = useEllipsisAnimation()

  useEffect(() => {
    const handler = () => {
      navLogoScrollRef.current = true
      setTimeout(() => {
        navLogoScrollRef.current = false
      }, 1200)
    }
    window.addEventListener("navLogoScrollTop", handler)
    return () => window.removeEventListener("navLogoScrollTop", handler)
  }, [])

  useEffect(() => {
    const handler = (evt: Event) => {
      const custom = evt as CustomEvent
      setStadiumLightsOn(Boolean(custom.detail?.on))
    }

    window.addEventListener("stadiumLightsChanged", handler as EventListener)
    return () => window.removeEventListener("stadiumLightsChanged", handler as EventListener)
  }, [])

  useEffect(() => {
    const handleReset = (_evt: Event) => {
      setStadiumLightsOn(false)
    }

    window.addEventListener("landingSequenceReset", handleReset as EventListener)
    return () => window.removeEventListener("landingSequenceReset", handleReset as EventListener)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width < 725)
      setIsDesktop(width >= 640)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (!arcRef.current || typeof window === "undefined" || !("IntersectionObserver" in window)) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return

        if (navLogoScrollRef.current) return

        const midScreen = entry.isIntersecting && entry.intersectionRatio >= 0.5
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0

        window.dispatchEvent(new CustomEvent("landingArcVisible", { detail: { isVisible: midScreen || scrolledPast } }))
      },
      {
        threshold: [0, 0.5] as number[],
        rootMargin: "0px 0px -50% 0px",
      },
    )

    observer.observe(arcRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!fansLiveRef.current || typeof window === "undefined" || !("IntersectionObserver" in window)) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        window.dispatchEvent(new CustomEvent("fansLiveVisible", { detail: { isVisible: entry.isIntersecting } }))
      },
      {
        threshold: 0.5,
        rootMargin: "0px 0px -40% 0px",
      },
    )

    observer.observe(fansLiveRef.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !sectionRef.current || !("IntersectionObserver" in window)) return

    const observer = new IntersectionObserver(() => undefined, { threshold: 1 })
    observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !askRef.current || !("IntersectionObserver" in window)) return

    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0]
        if (!e) return
        window.dispatchEvent(new CustomEvent("askBrackyVisible", { detail: { isVisible: e.isIntersecting } }))
      },
      { threshold: 0.25, rootMargin: "0px 0px -30% 0px" },
    )

    obs.observe(askRef.current)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !brackyHeroRef.current || !("IntersectionObserver" in window)) return

    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0]
        if (!e) return

        if (!typingStarted && e.isIntersecting && !heroSeenRef.current) {
          heroSeenRef.current = true
          setTimeout(() => setLiveSportsTrending(false), 5000)
        }

        setIsBrackyHeroVisible(e.isIntersecting)
      },
      { threshold: 0.3, rootMargin: "0px 0px -20% 0px" },
    )

    obs.observe(brackyHeroRef.current)
    return () => obs.disconnect()
  }, [typingStarted, heroSeenRef, setLiveSportsTrending])

  useEffect(() => {
    if (!typingStarted && ellipsisActive && !isMobile && isBrackyHeroVisible) {
      setLiveSportsTrending(true)
    } else if (!isBrackyHeroVisible) {
      setLiveSportsTrending(false)
    }
  }, [ellipsisActive, isMobile, isBrackyHeroVisible, typingStarted, setLiveSportsTrending])

  useEffect(() => {
    if (typeof window === "undefined") return

    const onScroll = () => {
      setHasScrolled(window.scrollY > 0)
    }

    onScroll()

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <section
      id="bracky"
      className={`relative isolate z-30 sm:px-6 w-full scroll-mt-[12vh] sm:scroll-mt-[16vh] lg:scroll-mt-[-3vh] xl:scroll-mt-[6vh] 2xl:scroll-mt-[6vh] tall:scroll-mt-[16vh] transition-opacity duration-300 ${_hasScrolled ? "opacity-100" : "opacity-0"}`}
    >
      <div ref={sectionRef} className="mx-auto">
        <div ref={arcRef} className="relative z-70 flex justify-center mb-16  sm:-mb-[12.6rem] opacity-100">
          <LandingArc className="w-full -z-40 max-w-4xl scale-95 sm:scale-105 lg:scale-[1.1] xl:scale-[1.3] 2xl:scale-[1.4] h-auto sm:-translate-y-20 md:-translate-y-8 mdlg:translate-y-0 lg:translate-y-[3.14rem] xl:translate-y-16 2xl:translate-y-18 blur-[30px] opacity-40" />
        </div>

        <div className="w-full h-8 sm:h-40 md:h-48 lg:h-56 -mt-20 mb-0 lg:mb-8" />

        <div ref={fansLiveRef} className="flex justify-center mb-[0.2rem] sm:mb-2 opacity-100">
          <div className="relative inline-block">
            <div className="relative z-10 pl-[.8rem] pr-4 mb-[-.76rem] sm:-mb-8 sm:-mt-[0.2rem] flex items-center gap-1.5 whitespace-nowrap">
              <LandingIconAnimatedActivity className="w-[.85rem] sm:w-[1.1rem] md:w-[1.15rem] md:h-[1.17rem] lg:w-[1.08rem]  xl:w-[1.08rem] lg:h-7 text-[#22FF65] relative" />
              <span className="text-[.875rem] sm:text-[1.04rem] font-normal tracking-wider">
                <span className="text-[#22FF65] -ml-[.1rem] font-semibold">{fanCount} </span>

                <span className="text-white/70">
                  <span className="sm:hidden">FANS BETTING LIVE IN THE FEED</span>
                  <span className="hidden sm:inline md:hidden">FANS BETTING LIVE IN THE FEED</span>
                  <span className="hidden md:inline lg:hidden">FANS BETTING LIVE IN FEED WITH BRACKY</span>
                  <span className="hidden lg:inline">FANS BETTING LIVE IN THE FEED WITH BRACKY</span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <div
          className={`relative w-full text-center mb-[1.3rem] sm:translate-y-[1.4rem] sm:mb-14 transition-opacity duration-300 ${_hasScrolled ? "opacity-100" : "opacity-0"}`}
        >
          <motion.div
            className="absolute mx-auto left-1/2 top-[calc(50%-rem)] sm:top-[calc(50%-.5rem)] w-[9.175rem] h-[9.175rem] sm:w-[12.165rem] sm:h-[12.165rem] md:w-[14.475rem] md:h-[14.475rem] lg:w-65 lg:h-65 pointer-events-none select-none -z-10"
            initial={{ opacity: 0, x: "-50%", y: "-20%", scale: 0.94 }}
            whileInView={{
              opacity: 0.12,
              x: "-50%",
              y: ["-50%", "-49%", "-50%"],
              scale: 1.07,
              transition: {
                duration: 0.8,
                ease: "easeOut",
                opacity: { duration: 0.6 },
                y: {
                  duration: 3,
                  ease: "easeInOut",
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: "reverse",
                },
              },
            }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img
              src="https://a.bracky.app/assets/landing/backgrounds/bracky-token-blue.webp"
              alt=""
              className="w-full h-full object-contain opacity-100"
              loading="lazy"
            />
          </motion.div>
        </div>

        <div ref={brackyHeroRef} className="relative z-20 text-center mb-[1.45rem] sm:mt-0 sm:mb-10">
          <BrackyHeroQuote
            liveSportsTrending={liveSportsTrending}
            ellipsisRef={ellipsisRef}
            onAnimationComplete={() => setEllipsisActive(true)}
          />
        </div>

        {/* Main Bracky Section CTA - positioned after hero quote */}
        <div className="flex flex-col items-center">
          <div className="*:mb-0!">
            <LandingCTA
              variant="inline"
              label={isDesktop ? (`Join ${fanCount} Betting Live` as string | undefined) : undefined}
            />
          </div>
          <p className="hidden sm:block mt-4 text-xs sm:text-sm text-white/50 text-center transition-opacity duration-300 ease-out">
            Sign in with X—Start betting live in seconds
          </p>
        </div>

        <LandingChipSectionHeader label="BRACKY 101" chipClassName="mb-3 mt-8 sm:mt-16 md:mt-24 lg:mt-25 xl:mt-34" />

        <div className="relative z-120">
          <LandingAskBracky ref={askRef} />
        </div>

        <div className="h-0 sm:h-8 md:h-12 lg:h-16 xl:h-20" />

        {brackyFeatures.slice(0, 4).map((feature, idx) => (
          <BrackyFeatureSection
            key={feature.title}
            feature={feature}
            index={idx}
            activeIdx={activeFeatureIdx}
            onSetActive={setActiveFeatureIdx}
          />
        ))}

        <LandingChipSectionHeader
          label={isMobile ? "AI SPORTS INTELLIGENCE™" : "UNLOCK YOUR AI EDGE"}
          chipClassName="mt-12 sm:mt-14 md:mt-14 mdlg:mt-18 mb-3 sm:mb-[-.2rem]"
        />

        <div className="flex flex-col items-center max-w-[90%] mx-auto mt-0 sm:mt-4 gap-3 lg:mb-[1.6rem]">
          <h2 className="text-white tracking-tight font-extrabold text-center text-h3 block md:hidden">
            Predict Sports' Biggest
            <br />
            Moments <span className="font-bold">with</span> The World's
            <br />
            <span className="font-extrabold mr-[-.1rem] italic">#1&nbsp;</span> Game-Aware AI.
          </h2>

          <h2 className="text-white tracking-tight font-extrabold text-center text-h3 hidden md:block mdlg:hidden">
            Predict Sports' Biggest
            <br />
            Moments <span className="font-bold">with</span> The World's
            <br />
            <span className="font-extrabold mr-[-.1rem] italic">#1&nbsp;</span> Game-Aware AI.
          </h2>

          <h2 className="text-white tracking-tight font-extrabold text-center text-h3 hidden mdlg:block lg:hidden">
            Predict Sports' Biggest
            <br />
            Moments <span className="font-bold">with</span> The World's
            <br />
            <span className="font-extrabold mr-[-.1rem] italic">#1&nbsp;</span> Game-Aware AI.
          </h2>

          <h2 className="text-white tracking-tight font-extrabold text-center text-h3 hidden lg:block">
            Predict Sports' Biggest Moments <span className="font-bold">with</span>
            <br />
            The World's <span className="font-extrabold mr-[-.1rem] italic">#1&nbsp;</span> Game-Aware AI.
          </h2>
        </div>

        <LandingBrackyAdvancedFeatures />
      </div>
    </section>
  )
}
